import { useState } from "react";
import { Image as ImageIcon } from "react-feather";
import type { UIMessage } from "../lib/types";
import { useSession } from "../store/session";
import { authFetch } from "../auth-context";
import { useAuthedImageUrl } from "../lib/useAuthedImageUrl";
import { assistantFileName } from "../lib/assistantFileName";
import { collectGallery } from "../lib/lightbox-gallery";
import ImageDownloadControl from "./ImageDownloadControl";
import DocumentAttachment from "./DocumentAttachment";
import "./UserAttachments.css";

export type UserAttachment = NonNullable<UIMessage["userAttachments"]>[number];
/** Display projection only. Server paths remain exact, including storage prefixes. */
export function userAttachments(message: Pick<UIMessage, "userAttachments" | "files">): UserAttachment[] {
  if (message.userAttachments?.length) return message.userAttachments;
  return (message.files ?? []).map(serverPath => {
    const filename = assistantFileName(serverPath), ext = filename.split(".").pop()?.toLowerCase();
    return { serverPath, filename, kind: ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "avif"].includes(ext ?? "") ? "image" : ext === "pdf" ? "pdf" : "file" };
  });
}
/** The same preview survives queued → confirmed → reloaded history. PDF/file cards
 * are downloads, not active embedded documents; image bytes use the existing auth hook. */
export default function UserAttachments({ attachments }: { attachments: readonly UserAttachment[] }) {
  if (!attachments.length) return null;
  return <div className="userAttachments" data-lb-group aria-label="Attachments">
    {attachments.map((attachment, i) => <Attachment key={attachment.serverPath || i} attachment={attachment} />)}
  </div>;
}
function Attachment({ attachment: a }: { attachment: UserAttachment }) {
  const openLightbox = useSession(s => s.openLightbox);
  const isImage = a.kind === "image";
  const { url, status } = useAuthedImageUrl(isImage && !a.blobUrl ? a.serverPath : undefined);
  const src = a.blobUrl ?? url;
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  async function download() {
    if (downloading) return;
    setDownloading(true); setError("");
    let blobUrl: string | undefined;
    try {
      const parts = a.serverPath.split("/").filter(Boolean);
      if (parts.length < 2 || /^https?:/i.test(a.serverPath)) throw Error("Invalid file location");
      const response = await authFetch(`/api/uploads/${encodeURIComponent(parts.at(-2)! )}/${encodeURIComponent(parts.at(-1)!)}`);
      if (!response.ok) throw Error("File unavailable");
      blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a"); link.href = blobUrl; link.download = a.filename;
      document.body.appendChild(link); link.click(); link.remove();
    } catch { setError("File unavailable. Please try again."); }
    finally {
      if (blobUrl) { const saved = blobUrl; window.setTimeout(() => URL.revokeObjectURL(saved), 60_000); }
      setDownloading(false);
    }
  }
  if (!isImage) return <div className="userAttachments__document">
    <DocumentAttachment attachment={{ name: a.filename, mime: a.mimeType || (a.kind === "pdf" ? "application/pdf" : undefined), size: a.sizeBytes }} onDownload={() => void download()} downloading={downloading} />
    {error && <p className="userAttachments__error" role="status">{error}</p>}
  </div>;
  return <div className="userAttachments__image">
    {src ? <><button type="button" className="userAttachments__preview" aria-label={`Open ${a.filename}`} onClick={event => {
      const gallery = collectGallery(event.currentTarget, { blobUrl: src, filename: a.filename }); openLightbox(gallery.items, gallery.index);
    }}><img src={src} alt={a.filename} loading="lazy" decoding="async" draggable={false} data-lb-src={src} data-lb-name={a.filename} /></button>
      <ImageDownloadControl src={src} filename={a.filename} mimeType={a.mimeType} /></>
      : <div className="userAttachments__placeholder"><ImageIcon size={22} aria-hidden="true" /><span>{status === "error" ? "Preview unavailable" : "Loading image…"}</span><small>{a.filename}</small></div>}
  </div>;
}

